import { SHAPE } from "./constants";
import { Rect } from "./math";
function documentToView(p, diagramRect, zoom, pan) {
    return {
        x: diagramRect.left + p.x * zoom - pan.x,
        y: diagramRect.top + p.y * zoom - pan.y
    };
}
function documentRectToView(r, diagramRect, zoom, pan) {
    const tl = documentToView({ x: r.x, y: r.y }, diagramRect, zoom, pan);
    return new Rect(tl.x, tl.y, r.width * zoom, r.height * zoom);
}
function nearestPointOnPolyline(poly, p) {
    let best = { point: poly[0], segIdx: 0, t: 0, dist2: Number.POSITIVE_INFINITY };
    for (let i = 0; i < poly.length - 1; i++) {
        const a = poly[i], b = poly[i + 1];
        const ab = { x: b.x - a.x, y: b.y - a.y };
        const ap = { x: p.x - a.x, y: p.y - a.y };
        const ab2 = ab.x * ab.x + ab.y * ab.y || 1e-9;
        const t = Math.max(0, Math.min(1, (ap.x * ab.x + ap.y * ab.y) / ab2));
        const proj = { x: a.x + ab.x * t, y: a.y + ab.y * t };
        const dx = p.x - proj.x, dy = p.y - proj.y, d2 = dx * dx + dy * dy;
        if (d2 < best.dist2)
            best = { point: proj, segIdx: i, t, dist2: d2 };
    }
    return best;
}
const GAP = 10;
const MARGIN = 8;
function candidateRect(pos, anchor, tipW, tipH) {
    switch (pos) {
        case 'right':
            return new Rect(anchor.x + GAP, anchor.y - tipH / 2, tipW, tipH);
        case 'left':
            return new Rect(anchor.x - GAP - tipW, anchor.y - tipH / 2, tipW, tipH);
        case 'bottom':
            return new Rect(anchor.x - tipW / 2, anchor.y + GAP, tipW, tipH);
        case 'top':
            return new Rect(anchor.x - tipW / 2, anchor.y - GAP - tipH, tipW, tipH);
    }
}
function overflowScore(r, bounds) {
    const leftOv = Math.max(0, bounds.left - r.x + MARGIN);
    const topOv = Math.max(0, bounds.top - r.y + MARGIN);
    const rightOv = Math.max(0, r.x + r.width - (bounds.right - MARGIN));
    const bottomOv = Math.max(0, r.y + r.height - (bounds.bottom - MARGIN));
    return leftOv + topOv + rightOv + bottomOv;
}
function segmentBufferRect(a, b, strokeHit) {
    const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
    return new Rect(minX - strokeHit / 2, minY - strokeHit / 2, maxX - minX + strokeHit, maxY - minY + strokeHit);
}
const positionsPriority = ['right', 'left', 'bottom', 'top'];
/**
 * Calculates the optimal position for a tooltip relative to a diagram shape or connection.
 *
 * This function handles coordinate transformations between document space (diagram model coordinates)
 * and view space (browser viewport coordinates), accounting for zoom and pan transformations.
 * It also performs collision detection and viewport boundary clamping.
 *
 * @param options - Configuration options for tooltip placement
 * @param options.hovered - The diagram shape or connection being hovered
 * @param options.mouse - Mouse position in document coordinates (diagram model space)
 * @param options.shapes - Array of shape bounds in document coordinates
 * @param options.connections - Array of connection polylines in document coordinates
 * @param options.diagramRect - The diagram's bounding rectangle in view space
 * @param options.zoom - Current zoom level of the diagram
 * @param options.pan - Current pan offset of the diagram (as returned by diagram.pan())
 * @param options.tooltipSize - Width and height of the tooltip element
 * @param options.viewportBounds - Browser viewport bounds for clamping
 * @returns Position object with left, top coordinates in view space and the chosen position side
 */
export function placeTooltip(options) {
    const { hovered, shapes, connections, diagramRect, zoom, pan, tooltipSize, viewportBounds, mouse } = options;
    const boundsForFit = viewportBounds;
    let anchorView;
    let shapeAnchors;
    if (hovered.name === SHAPE) {
        const rDocument = hovered.bounds();
        const rView = documentRectToView(rDocument, diagramRect, zoom, pan);
        const anchors = {
            right: { x: rView.x + rView.width, y: rView.y + rView.height / 2 },
            left: { x: rView.x, y: rView.y + rView.height / 2 },
            bottom: { x: rView.x + rView.width / 2, y: rView.y + rView.height },
            top: { x: rView.x + rView.width / 2, y: rView.y }
        };
        shapeAnchors = anchors;
    }
    else {
        const poly = hovered.allPoints();
        const near = nearestPointOnPolyline(poly, mouse);
        anchorView = documentToView(near.point, diagramRect, zoom, pan);
    }
    const shapeRectsView = shapes.map((r) => documentRectToView(r, diagramRect, zoom, pan));
    const strokeHit = 10; // px
    const segRectsView = [];
    for (const poly of connections) {
        for (let i = 0; i < poly.length - 1; i++) {
            const aView = documentToView(poly[i], diagramRect, zoom, pan);
            const bView = documentToView(poly[i + 1], diagramRect, zoom, pan);
            segRectsView.push(segmentBufferRect(aView, bView, strokeHit));
        }
    }
    const candidates = [];
    for (const pos of positionsPriority) {
        const anchor = hovered.name === SHAPE ? shapeAnchors[pos] : anchorView;
        const rect = candidateRect(pos, anchor, tooltipSize.width, tooltipSize.height);
        const ov = overflowScore(rect, boundsForFit);
        let hits = 0;
        for (const r of shapeRectsView)
            if (rect.overlaps(r)) {
                hits++;
                break;
            }
        for (const r of segRectsView)
            if (rect.overlaps(r)) {
                hits++;
                break;
            }
        candidates.push({ pos, rect, ov, hits });
    }
    candidates.sort((a, b) => a.ov - b.ov || a.hits - b.hits || positionsPriority.indexOf(a.pos) - positionsPriority.indexOf(b.pos));
    const best = candidates[0];
    const finalRect = best.ov === 0 ? best.rect : clampRect(best.rect, boundsForFit);
    return { left: finalRect.x, top: finalRect.y, position: best.pos };
}
function clampRect(r, bounds) {
    let x = r.x, y = r.y;
    if (x < bounds.left + MARGIN)
        x = bounds.left + MARGIN;
    if (y < bounds.top + MARGIN)
        y = bounds.top + MARGIN;
    if (x + r.width > bounds.right - MARGIN)
        x = bounds.right - MARGIN - r.width;
    if (y + r.height > bounds.bottom - MARGIN)
        y = bounds.bottom - MARGIN - r.height;
    return new Rect(x, y, r.width, r.height);
}
